import {
  BrandContentDto,
  CreatePieceDto,
  EditPieceDto,
  EditSlidesDto,
  PieceDto,
  PieceSummaryDto,
  RejectDto,
  TaskDto,
} from "@growth/dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

/** Agents work in the background: anything not settled is polled until it is. */
const working = (piece: PieceDto | undefined) =>
  !piece ||
  piece.status === "drafting" ||
  piece.tasks.some((task) => task.busy) ||
  Boolean(piece.carousel?.rendering);

export const usePieces = () =>
  useQuery({
    queryKey: ["pieces"],
    queryFn: async () => (await api.get<PieceSummaryDto[]>("/pieces")).data,
    refetchInterval: (query) =>
      query.state.data?.some((piece) => piece.status === "drafting") ? 2000 : 15_000,
  });

export const usePiece = (id: string) =>
  useQuery({
    queryKey: ["pieces", id],
    queryFn: async () => (await api.get<PieceDto>(`/pieces/${id}`)).data,
    refetchInterval: (query) => (working(query.state.data) ? 1500 : false),
  });

export const useTasks = () =>
  useQuery({
    queryKey: ["tasks"],
    queryFn: async () => (await api.get<TaskDto[]>("/tasks")).data,
    refetchInterval: 5000,
  });

const useRefresh = () => {
  const queryClient = useQueryClient();
  return (piece: PieceDto) => {
    queryClient.setQueryData(["pieces", piece.id], piece);
    void queryClient.invalidateQueries({ queryKey: ["pieces"], exact: true });
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
  };
};

export const useCreatePiece = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePieceDto) =>
      (await api.post<PieceSummaryDto>("/pieces", input)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pieces"], exact: true }),
  });
};

export const useEditPiece = (id: string) => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (input: EditPieceDto) =>
      (await api.patch<PieceDto>(`/pieces/${id}`, input)).data,
    onSuccess: refresh,
  });
};

export const useEditSlides = (id: string) => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (input: EditSlidesDto) =>
      (await api.put<PieceDto>(`/pieces/${id}/slides`, input)).data,
    onSuccess: refresh,
  });
};

type TaskAction =
  | { kind: "approve"; taskId: string }
  | { kind: "send"; taskId: string }
  | { kind: "reject"; taskId: string; input: RejectDto }
  | { kind: "posted"; taskId: string; url: string };

export const useTaskAction = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (action: TaskAction) => {
      const base = `/tasks/${action.taskId}`;
      if (action.kind === "approve") return (await api.post<PieceDto>(`${base}/approve`)).data;
      if (action.kind === "send")
        return (await api.post<PieceDto>(`${base}/send-for-approval`)).data;
      if (action.kind === "reject")
        return (await api.post<PieceDto>(`${base}/reject`, action.input)).data;
      return (await api.post<PieceDto>(`${base}/posted`, { url: action.url })).data;
    },
    onSuccess: refresh,
  });
};

export const useContentSettings = () =>
  useQuery({
    queryKey: ["content-settings"],
    queryFn: async () => (await api.get<BrandContentDto>("/content-settings")).data,
  });

export const useSaveContentSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BrandContentDto) =>
      (await api.put<BrandContentDto>("/content-settings", input)).data,
    onSuccess: (settings) => queryClient.setQueryData(["content-settings"], settings),
  });
};
